import { useEffect, useRef, useState } from 'react'
import './PhoneInput.css'

export interface Country {
  code: string
  dial: string
  name: string
}

// Lista curata: Italia (default) + paesi rilevanti per il territorio.
export const COUNTRIES: Country[] = [
  { code: 'IT', dial: '+39', name: 'Italia' },
  { code: 'SM', dial: '+378', name: 'San Marino' },
  { code: 'CH', dial: '+41', name: 'Svizzera' },
  { code: 'AT', dial: '+43', name: 'Austria' },
  { code: 'AL', dial: '+355', name: 'Albania' },
  { code: 'RO', dial: '+40', name: 'Romania' },
  { code: 'MD', dial: '+373', name: 'Moldavia' },
]

/** Bandierina SVG (20x14), arrotondata via .flag (overflow:hidden + radius). */
export function Flag({ code }: { code: string }) {
  switch (code) {
    case 'RU':
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="20" height="4.67" fill="#fff" /><rect y="4.67" width="20" height="4.66" fill="#0039A6" /><rect y="9.33" width="20" height="4.67" fill="#D52B1E" /></svg></span>
      )
    case 'RO':
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="6.67" height="14" fill="#002B7F" /><rect x="6.67" width="6.66" height="14" fill="#FCD116" /><rect x="13.33" width="6.67" height="14" fill="#CE1126" /></svg></span>
      )
    case 'MD':
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="6.67" height="14" fill="#0046AE" /><rect x="6.67" width="6.66" height="14" fill="#FFD200" /><rect x="13.33" width="6.67" height="14" fill="#CC092F" /><path d="M10 4.5l1.6 1.1v2.2L10 9.5 8.4 7.8V5.6z" fill="#A0712C" opacity="0.85" /></svg></span>
      )
    case 'AT':
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="20" height="4.67" fill="#ED2939" /><rect y="4.67" width="20" height="4.66" fill="#fff" /><rect y="9.33" width="20" height="4.67" fill="#ED2939" /></svg></span>
      )
    case 'AL':
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="20" height="14" fill="#E41E20" /><path d="M10 3.4 8.7 4.6 6.8 3.9l1 1.7-2 .5 1.9.9-1.2 1.6 2-.5.1 1.9 1.4-1.3 1.4 1.3.1-1.9 2 .5-1.2-1.6 1.9-.9-2-.5 1-1.7-1.9.7z" fill="#1A1A1A" /></svg></span>
      )
    case 'SM':
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="20" height="7" fill="#fff" /><rect y="7" width="20" height="7" fill="#5EB6E4" /></svg></span>
      )
    case 'CH':
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="20" height="14" fill="#D52B1E" /><rect x="8.5" y="3" width="3" height="8" fill="#fff" /><rect x="6" y="5.5" width="8" height="3" fill="#fff" /></svg></span>
      )
    case 'IT':
    default:
      return (
        <span className="flag"><svg viewBox="0 0 20 14"><rect width="6.67" height="14" fill="#009246" /><rect x="6.67" width="6.66" height="14" fill="#fff" /><rect x="13.33" width="6.67" height="14" fill="#CE2B37" /></svg></span>
      )
  }
}

interface PhoneInputProps {
  id?: string
  dialCode: string
  onDialCodeChange: (dial: string) => void
  value: string
  onChange: (numero: string) => void
  disabled?: boolean
  /** Sottoinsieme di paesi selezionabili (default: tutti). Con un solo paese il prefisso è fisso. */
  countries?: Country[]
}

/**
 * Input-group telefono: selettore prefisso con bandiera (Italia default) + numero.
 * Stile coerente con .cform (border, radius, focus navy).
 */
export function PhoneInput({ id, dialCode, onDialCodeChange, value, onChange, disabled, countries }: PhoneInputProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const list = countries ?? COUNTRIES
  const canPick = list.length > 1
  const selected = list.find((c) => c.dial === dialCode) ?? list[0]

  useEffect(() => {
    if (!open || disabled) return
    function onPointerDown(e: PointerEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className={'phone-group' + (disabled ? ' is-disabled' : '')} ref={containerRef}>
      <button
        type="button"
        className="phone-cc"
        aria-haspopup={canPick ? 'listbox' : undefined}
        aria-expanded={canPick ? open : undefined}
        aria-label={`Prefisso internazionale: ${selected.name} ${selected.dial}`}
        onClick={() => { if (canPick) setOpen((v) => !v) }}
        disabled={disabled}
      >
        <Flag code={selected.code} />
        <span className="dial">{selected.dial}</span>
        {canPick && <svg className="chev" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      </button>

      {open && (
        <ul className="phone-menu" role="listbox">
          {list.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                role="option"
                aria-selected={c.dial === dialCode}
                className={'phone-opt' + (c.dial === dialCode ? ' is-active' : '')}
                onClick={() => {
                  onDialCodeChange(c.dial)
                  setOpen(false)
                }}
              >
                <Flag code={c.code} />
                <span className="name">{c.name}</span>
                <span className="dial">{c.dial}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        id={id}
        className="phone-num"
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder="333 123 4567"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required
      />
    </div>
  )
}
